import { isAbortError } from "@/shared/errors";

import { pickNextSource, type PendingSource } from "./pickNextSource";

export interface SourceIndexSchedulerParams {
  /** Sources to index, each carrying its timeline range for playhead ranking. */
  pending: PendingSource[];
  /** Reads the live playhead on every pick, so a mid-fill seek re-anchors the
   *  remaining picks at the new region. */
  getPlayhead: () => bigint;
  /** Index one source: fetch its MCAP footer/summary into chunk indices and
   *  merge them into the playback index. */
  indexSource: (source: PendingSource, signal: AbortSignal) => Promise<void>;
  /** Fired once, when the first source at or before the playhead is indexed —
   *  the cue to draw the first frame while the rest keep streaming in. */
  onFirstIndexed: (signal: AbortSignal) => Promise<void> | void;
  concurrency: number;
}

/**
 * Indexes a set of media sources with bounded concurrency, always picking the
 * source nearest the live playhead so the first frame draws as soon as the
 * covering source is indexed and the fill re-anchors when the playhead moves.
 *
 * This bakes in one policy — playhead-anchored ranking, a concurrency cap, and
 * a first-indexed kick — rather than being a generic task runner: the dynamic,
 * seek-reactive behavior is the whole point and is what a generic scheduler
 * couldn't express. It reads the playhead fresh on each pick (not a queue
 * computed once), so seeking mid-fill pulls a still-pending source at the new
 * region ahead on the next freed slot.
 */
export class SourceIndexScheduler {
  #params: SourceIndexSchedulerParams;

  constructor(params: SourceIndexSchedulerParams) {
    this.#params = params;
  }

  public async run(signal: AbortSignal): Promise<void> {
    const { getPlayhead, indexSource, onFirstIndexed, concurrency } =
      this.#params;
    const remaining = [...this.#params.pending];
    const inFlight = new Set<Promise<void>>();
    let firstKicked = false;
    let failure: Error | undefined;

    const launch = () => {
      const index = pickNextSource(remaining, getPlayhead());
      if (index === undefined) {
        return;
      }
      const [source] = remaining.splice(index, 1);

      const task = indexSource(source, signal)
        .then(() => {
          if (signal.aborted || firstKicked) {
            return undefined;
          }
          // Kick the first frame once a source at or before the playhead is
          // indexed — that source contributes the frame under (or last before)
          // the playhead. Ranking opens such a source first, so this fires
          // early; later sources don't re-fire it.
          if (source.startTime <= getPlayhead()) {
            firstKicked = true;
            return onFirstIndexed(signal);
          }
          return undefined;
        })
        .catch((err) => {
          if (!signal.aborted && !isAbortError(err) && failure === undefined) {
            failure = err instanceof Error ? err : new Error(String(err));
          }
        })
        .finally(() => {
          inFlight.delete(task);
        });
      inFlight.add(task);
    };

    while (
      !signal.aborted &&
      failure === undefined &&
      (remaining.length > 0 || inFlight.size > 0)
    ) {
      while (
        !signal.aborted &&
        failure === undefined &&
        inFlight.size < concurrency &&
        remaining.length > 0
      ) {
        launch();
      }
      if (inFlight.size === 0) {
        break;
      }
      await Promise.race(inFlight);
    }

    if (failure !== undefined && !signal.aborted) {
      throw failure;
    }
  }
}
